import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/context/LanguageContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = t('contact.validation.name');
    if (!EMAIL_RE.test(values.email.trim())) next.email = t('contact.validation.email');
    if (!values.message.trim()) next.message = t('contact.validation.message');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    // Frontend-only: no network call. Redirect to the Spanish-only thank you page.
    setSubmitting(true);
    navigate('/thank_you');
  };

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="contact-form" className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="cf-name">{t('contact.form.name')}</Label>
        <Input
          id="cf-name"
          value={values.name}
          onChange={setField('name')}
          placeholder={t('contact.form.namePlaceholder')}
          data-testid="contact-form-name-input"
          aria-invalid={!!errors.name}
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="cf-email">{t('contact.form.email')}</Label>
        <Input
          id="cf-email"
          type="email"
          value={values.email}
          onChange={setField('email')}
          placeholder={t('contact.form.emailPlaceholder')}
          data-testid="contact-form-email-input"
          aria-invalid={!!errors.email}
        />
        {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="cf-message">{t('contact.form.message')}</Label>
        <Textarea
          id="cf-message"
          rows={5}
          value={values.message}
          onChange={setField('message')}
          placeholder={t('contact.form.messagePlaceholder')}
          data-testid="contact-form-message-textarea"
          aria-invalid={!!errors.message}
        />
        {errors.message && <p className="text-xs text-destructive">{errors.message}</p>}
      </div>

      <Button
        type="submit"
        disabled={submitting}
        data-testid="contact-form-submit-button"
        className="w-full bg-[hsl(var(--inv-blue))] text-[hsl(var(--primary-foreground))] transition-colors hover:bg-[hsl(var(--inv-blue-2))] active:scale-[0.99]"
      >
        {submitting ? t('contact.form.sending') : t('contact.form.submit')}
      </Button>
    </form>
  );
}

export default ContactForm;
